import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    FaUser,
    FaGraduationCap,
    FaBriefcase,
    FaCamera,
    FaSave,
    FaFileUpload,
    FaCheckCircle,
    FaGithub,
    FaLinkedin,
    FaGlobe
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './StudentProfile.css';

const StudentProfile = () => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState({
        name: '',
        phone: '',
        bio: '',
        location: '',
        profileImage: '',
        academicInfo: {
            education: '',
            skills: [],
            github: '',
            linkedin: ''
        },
        careerPreferences: {
            preferredJobTypes: [],
            preferredLocations: [],
            expectedSalary: ''
        },
        resumeUrl: ''
    });

    const [inputStates, setInputStates] = useState({
        skills: '',
        preferredJobTypes: '',
        preferredLocations: ''
    });

    const fileInputRef = useRef(null);
    const resumeInputRef = useRef(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                const data = res.data;
                setProfile({
                    name: data.name || '',
                    phone: data.phone || '',
                    bio: data.bio || '',
                    location: data.location || '',
                    profileImage: data.profileImage || '',
                    academicInfo: {
                        education: data.academicInfo?.education || '',
                        skills: data.academicInfo?.skills || [],
                        github: data.academicInfo?.github || '',
                        linkedin: data.academicInfo?.linkedin || ''
                    },
                    careerPreferences: {
                        preferredJobTypes: data.careerPreferences?.preferredJobTypes || [],
                        preferredLocations: data.careerPreferences?.preferredLocations || [],
                        expectedSalary: data.careerPreferences?.expectedSalary || ''
                    },
                    resumeUrl: data.resumeUrl || ''
                });

                setInputStates({
                    skills: (data.academicInfo?.skills || []).join(', '),
                    preferredJobTypes: (data.careerPreferences?.preferredJobTypes || []).join(', '),
                    preferredLocations: (data.careerPreferences?.preferredLocations || []).join(', ')
                });

                setLoading(false);
            } catch (err) {
                console.error("Error fetching student profile", err);
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [section, field] = name.split('.');
            setProfile(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setProfile(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleArrayInputChange = (section, field, value) => {
        setInputStates(prev => ({ ...prev, [field]: value }));

        const items = value.split(',').map(s => s.trim()).filter(s => s !== '');
        setProfile(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: items
            }
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        showLoading('Uploading Image...');
        try {
            const res = await axios.post('/api/users/profile-image', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, profileImage: res.data.profileImage }));
            closeLoading();
            showSuccess('Success', 'Profile image updated.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            closeLoading();
            showError('Failed', 'Could not upload image.');
        }
    };

    const handleResumeUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('resume', file);
        showLoading('Uploading Resume...');
        try {
            const res = await axios.post('/api/users/resume-upload', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, resumeUrl: res.data.resumeUrl }));
            closeLoading();
            showSuccess('Success', 'Resume uploaded and verified.');
        } catch (err) {
            closeLoading();
            showError('Failed', 'Resume upload failed.');
        }
    };

    const handleViewResume = () => {
        if (profile.resumeUrl) {
            window.open(`${API_URL}${profile.resumeUrl}`, '_blank');
        } else {
            showError('No Resume', 'You have not uploaded a resume yet.');
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoading('Synchronizing Profile...');
        try {
            await axios.put('/api/users/profile', profile, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            closeLoading();
            showSuccess('Synced', 'Your professional profile is now synced with the Candidate Database.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            closeLoading();
            showError('Update Failed', 'Failed to synchronize profile data.');
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="student-profile fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Professional Identity</h1>
                <p className="page-subtitle">Manage your academic credentials and career preferences for enterprise matching.</p>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="profile-sections-grid">
                    {/* Left Panel */}
                    <div className="profile-left-panel">
                        <div className="profile-card text-center">
                            <div className="avatar-upload">
                                <div className="avatar-large">
                                    {profile.profileImage ? (
                                        <img src={`${API_URL}${profile.profileImage}`} alt="Profile" />
                                    ) : (
                                        profile.name?.charAt(0).toUpperCase()
                                    )}
                                </div>
                                <button type="button" className="cam-btn" onClick={() => fileInputRef.current.click()}>
                                    <FaCamera />
                                </button>
                                <input type="file" ref={fileInputRef} hidden onChange={handleImageUpload} accept="image/*" />
                            </div>
                            <h2 style={{ fontSize: '20px', fontWeight: 700 }}>{profile.name}</h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>Student / Job Seeker</p>

                            <div style={{ textAlign: 'left', marginTop: '20px' }}>
                                <div className="sync-status">
                                    <FaCheckCircle /> Profile Synced to Candidate DB
                                </div>
                            </div>
                        </div>

                        <div className="profile-card" style={{ marginTop: '24px' }}>
                            <h3 className="section-title" style={{ fontSize: '15px' }}><FaFileUpload /> Professional Assets</h3>
                            <div className="resume-strip">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '24px', color: '#0284C7' }}><FaFileUpload /></span>
                                    <div>
                                        <p style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>Resume / CV</p>
                                        <p style={{ fontSize: '11px', color: '#64748B', margin: 0 }}>
                                            {profile.resumeUrl ? 'Verified Document' : 'No document uploaded'}
                                        </p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {profile.resumeUrl && (
                                        <button
                                            type="button"
                                            className="join-btn"
                                            onClick={handleViewResume}
                                            style={{ background: '#002C5F', width: 'auto', padding: '0 12px' }}
                                            title="View Resume"
                                        >
                                            View
                                        </button>
                                    )}
                                    <button type="button" className="join-btn" onClick={() => resumeInputRef.current.click()} style={{ background: '#0284C7' }}>
                                        {profile.resumeUrl ? 'Replace' : 'Upload'}
                                    </button>
                                </div>
                                <input type="file" ref={resumeInputRef} hidden onChange={handleResumeUpload} accept=".pdf,.doc,.docx" />
                            </div>
                        </div>
                    </div>

                    {/* Right Panel */}
                    <div className="profile-right-panel">
                        <div className="section-card">
                            <h3 className="section-title"><FaUser /> Personal Intelligence</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Full Identity</label>
                                    <input className="glass-input" name="name" value={profile.name} onChange={handleChange} placeholder="e.g. Abhishek Kumar" />
                                </div>
                                <div className="form-group">
                                    <label>Contact Vector (Phone)</label>
                                    <input className="glass-input" name="phone" value={profile.phone} onChange={handleChange} placeholder="e.g. +91 98765 43210" />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Geographic Hub (Location)</label>
                                <input className="glass-input" name="location" value={profile.location} onChange={handleChange} placeholder="e.g. Bangalore, India" />
                            </div>
                            <div className="form-group">
                                <label>Professional Brief (Bio)</label>
                                <textarea className="glass-input resize-none" name="bio" value={profile.bio} onChange={handleChange} rows="3" placeholder="Describe your professional background, interests, and goals in 2-3 sentences..."></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="section-card full-width-card">
                        <h3 className="section-title"><FaGraduationCap /> Academic Credentials</h3>
                        <div className="form-group">
                            <label>Highest Qualification</label>
                            <input className="glass-input" name="academicInfo.education" value={profile.academicInfo.education} onChange={handleChange} placeholder="e.g. B.Tech Computer Science, XYZ University (2024)" />
                        </div>
                        <div className="form-group">
                            <label>Skill Matrix (Comma separated)</label>
                            <input
                                className="glass-input"
                                value={inputStates.skills}
                                onChange={(e) => handleArrayInputChange('academicInfo', 'skills', e.target.value)}
                                placeholder="React, Node.js, Python..."
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label><FaGithub /> GitHub URL</label>
                                <input className="glass-input" name="academicInfo.github" value={profile.academicInfo.github} onChange={handleChange} placeholder="https://github.com/your-username" />
                            </div>
                            <div className="form-group">
                                <label><FaLinkedin /> LinkedIn URL</label>
                                <input className="glass-input" name="academicInfo.linkedin" value={profile.academicInfo.linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/your-profile" />
                            </div>
                        </div>
                    </div>

                    <div className="section-card full-width-card">
                        <h3 className="section-title"><FaBriefcase /> Career Preferences</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Target Job Roles (Comma separated)</label>
                                <input
                                    className="glass-input"
                                    value={inputStates.preferredJobTypes}
                                    onChange={(e) => handleArrayInputChange('careerPreferences', 'preferredJobTypes', e.target.value)}
                                    placeholder="Full Stack Developer, UI Designer..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Compensation Target (Expected Salary)</label>
                                <input className="glass-input" name="careerPreferences.expectedSalary" value={profile.careerPreferences.expectedSalary} onChange={handleChange} placeholder="e.g. $80,000 per annum" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Preferred Geographies (Comma separated locations)</label>
                            <input
                                className="glass-input"
                                value={inputStates.preferredLocations}
                                onChange={(e) => handleArrayInputChange('careerPreferences', 'preferredLocations', e.target.value)}
                                placeholder="Remote, Bangalore, Mumbai..."
                            />
                        </div>
                    </div>

                    <div className="full-width-card" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '32px' }}>
                        <button type="submit" className="primary-btn pulse-on-hover" style={{ padding: '12px 32px' }}>
                            <FaSave /> Save & Synchronize
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default StudentProfile;
